import type { DisciplineScheduleDay } from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useAdminNow } from '@entities/viewer'
import { useDayjs } from '@shared/lib/format'

import { DisciplineScheduleFlow } from './enums'
import { useDisciplineScheduleStore } from './schedule/store'
import { useDisciplineScheduleContext } from './discipline-schedule-context.hook'
import {
  formatScheduleDay,
  formatSchedulePace,
  isSchedulePending,
  sortScheduleDays,
} from './discipline-schedule'

interface DisciplineWeeklyScheduleReturn {
  refPaceText: ComputedRef<string>
  refDays: ComputedRef<string[]>
  refHasSchedule: ComputedRef<boolean>
  refStartDate: ComputedRef<string | null>
  refIsCreateDisabled: ComputedRef<boolean>
  openCreate: () => void
}

/** The weekly lesson slots of a DEAL customer's discipline, in the customer's timezone. */
export const useDisciplineWeeklySchedule = (): DisciplineWeeklyScheduleReturn => {
  const dayjs = useDayjs()
  const store = useDisciplineScheduleStore()
  const { refDetails, refCustomerTimezone } = useDisciplineScheduleContext()

  const refPaceText = computed<string>(() => {
    const details = refDetails.value
    if (!details?.pace) return ''

    const pace = formatSchedulePace(details.pace, details.code)
    return refCustomerTimezone.value ? `${pace} (${refCustomerTimezone.value})` : pace
  })

  const refScheduleDays = computed<DisciplineScheduleDay[]>(() => sortScheduleDays(refDetails.value?.schedule?.days ?? []))

  const refDays = computed<string[]>(() => refScheduleDays.value.map(formatScheduleDay))

  const refHasSchedule = computed<boolean>(() => refScheduleDays.value.length > 0)

  /** A changed schedule shows when it takes over; the API reports the date in the admin's view timezone. */
  const refStartDate = computed<string | null>(() => {
    const effectiveDate = refDetails.value?.schedule?.effective_date
    if (!isSchedulePending(effectiveDate, useAdminNow().format(DateFormat.Default))) return null

    return dayjs(effectiveDate).format(DateFormat.KanbanDate)
  })

  const refIsCreateDisabled = computed<boolean>(() => refDetails.value?.balance === 0)

  const openCreate = (): void => {
    store.openFlow(DisciplineScheduleFlow.Create)
  }

  return {
    refPaceText,
    refDays,
    refHasSchedule,
    refStartDate,
    refIsCreateDisabled,
    openCreate,
  }
}
